import React, { useEffect, useState, useMemo, useRef } from "react";
import { ListTree, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import type { ArticleHeading } from "@/lib/article";

interface ScrollSpyTocProps {
  headings?: ArticleHeading[];
  content?: string;
  className?: string;
}

export function ScrollSpyToc({ headings: propHeadings, content, className }: ScrollSpyTocProps) {
  const [activeId, setActiveId] = useState<string>("");
  const listRef = useRef<HTMLUListElement>(null);

  // Pure memoized headings resolution: either from props or parsed directly from processed HTML content
  const headings = useMemo<ArticleHeading[]>(() => {
    if (propHeadings && propHeadings.length > 0) return propHeadings;
    if (!content) return [];

    const list: ArticleHeading[] = [];
    const regex = /<h([23])[^>]*id=["']([^"']*)["'][^>]*>(.*?)<\/h\1>/gi;
    let match: RegExpExecArray | null;
    while ((match = regex.exec(content)) !== null) {
      const level = parseInt(match[1], 10) as 2 | 3;
      const id = match[2];
      const text = match[3].replace(/<[^>]+>/g, "").trim();
      if (text && id) {
        list.push({ id, text, level });
      }
    }
    return list;
  }, [propHeadings, content]);

  // Real-time scroll spy tracking: triggers instantaneously as sections cross the reading line
  useEffect(() => {
    if (headings.length === 0 || typeof window === "undefined") return;

    let ticking = false;

    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          // Reading line located 130px below top of viewport (accounting for fixed header)
          const scrollPos = window.scrollY + 130;

          let currentActiveId = headings[0]?.id || "";

          for (let i = 0; i < headings.length; i++) {
            const h = headings[i];
            const el = document.getElementById(h.id);
            if (el) {
              const top = el.getBoundingClientRect().top + window.scrollY;
              if (top <= scrollPos) {
                currentActiveId = h.id;
              } else {
                break;
              }
            }
          }

          setActiveId((prev) => (prev !== currentActiveId ? currentActiveId : prev));
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    // Initial check
    onScroll();

    return () => window.removeEventListener("scroll", onScroll);
  }, [headings]);

  const scrollToHeading = (id: string, e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    let target = document.getElementById(id);
    if (!target) {
      target = document.querySelector(`#article-content #${CSS.escape(id)}`);
    }
    if (!target) {
      const container = document.getElementById("article-content");
      if (container) {
        const item = headings.find((h) => h.id === id);
        if (item) {
          const allH = container.querySelectorAll("h2, h3");
          for (const h of Array.from(allH)) {
            if (h.textContent?.trim() === item.text) {
              target = h as HTMLElement;
              break;
            }
          }
        }
      }
    }

    if (target) {
      const yOffset = -95;
      const y = target.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: Math.max(0, y), behavior: "smooth" });
      setActiveId(id);

      if (window.history && window.history.replaceState) {
        window.history.replaceState(null, "", `#${id}`);
      }
    }
  };

  if (headings.length === 0) return null;

  return (
    <nav
      aria-label="Table of contents"
      className={cn(
        "rounded-2xl border border-border/70 bg-card/60 backdrop-blur-md p-5 text-sm space-y-4 shadow-sm",
        className,
      )}
    >
      <div className="flex items-center justify-between pb-3 border-b border-border/50">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-foreground">
          <ListTree className="h-4 w-4 text-primary" />
          <span>Contents</span>
        </div>
        <span className="text-[11px] font-semibold text-muted-foreground bg-accent/60 px-2 py-0.5 rounded-full">
          {headings.length} sections
        </span>
      </div>

      <ul
        ref={listRef}
        className="space-y-1 max-h-[calc(100vh-14rem)] overflow-y-auto pr-1 scrollbar-hide relative"
      >
        {headings.map((h, i) => {
          const isActive = activeId === h.id;
          return (
            <li
              key={h.id}
              style={{ paddingLeft: h.level === 3 ? "0.75rem" : "0" }}
              className="relative"
            >
              <a
                href={`#${h.id}`}
                onClick={(e) => scrollToHeading(h.id, e)}
                className={cn(
                  "relative w-full text-left py-1.5 px-3 rounded-xl text-xs transition-all duration-200 flex items-center justify-between group cursor-pointer select-none",
                  isActive
                    ? "font-bold text-primary bg-primary/10 shadow-sm border border-primary/20"
                    : "text-muted-foreground hover:text-foreground hover:bg-accent/60 font-medium",
                )}
                aria-current={isActive ? "location" : undefined}
              >
                {/* Active Left Vertical Accent Bar */}
                {isActive && (
                  <span
                    className="absolute left-0 top-1.5 bottom-1.5 w-1 rounded-r-full bg-primary"
                    aria-hidden="true"
                  />
                )}

                <div className="flex items-center gap-2 min-w-0 pl-1">
                  <span
                    className={cn(
                      "text-[10px] font-mono shrink-0 transition-colors",
                      isActive ? "text-primary font-bold" : "text-muted-foreground/60",
                    )}
                  >
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="truncate">{h.text}</span>
                </div>
                {isActive && (
                  <ChevronRight className="h-3 w-3 shrink-0 text-primary animate-pulse" />
                )}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
